import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiCorePlayerDimsSqlMap } from '../sql-maps/bicore-player-dims-datasource-sql-map';
import { BiCoreProjectSessionsSqlMap } from '../sql-maps/bicore-project-sessions-datasource-sql-map';

export interface RoyaltiesQueryResult {
    timePerGame: Record<string, unknown>[];
    contentToTotal: Record<string, unknown>[];
}

export class RoyaltiesDataLoader {
    /**
     * Runs three sequential BigQuery queries for the previous calendar month royalties report:
     *  1. Distinct project IDs – to build the PIVOT column list
     *  2. time_per_game_minutes – wide-format pivot, one column per project
     *  3. time_game_content_to_total – daily playtime breakdown
     */
    static async fetchData(
        gcpProjectId: GcpProjectId,
        queryCache: QueryCacheService,
        dateFrom: string,
        dateTo: string
    ): Promise<RoyaltiesQueryResult> {
        const projectSessionsSqlMap = BiCoreProjectSessionsSqlMap;
        const playerDimsSqlMap = BiCorePlayerDimsSqlMap;

        const baseQuery = `
    SELECT FORMAT_DATE('%Y-%m-%d', DATE(start_time)) AS date
      , IF( project_id = '_NULL_', 'Unsolved', project_id ) AS project_id
      , SUM(TIMESTAMP_DIFF(end_time, start_time, SECOND)/60) AS time
    FROM \`${generateTableFullPath(
        gcpProjectId,
        projectSessionsSqlMap.sqlMap.dataset,
        projectSessionsSqlMap.sqlMap.tableId
    )}\` s
    JOIN \`${generateTableFullPath(
        gcpProjectId,
        playerDimsSqlMap.sqlMap.dataset,
        playerDimsSqlMap.sqlMap.tableId
    )}\` d
      ON s.player_id = d.player_id
    WHERE DATE(start_time) BETWEEN '${dateFrom}' AND '${dateTo}'
      AND d.validation.unified_valid
    GROUP BY project_id, date
`;

        const projectIdsSql = `
WITH
  sessions AS (
${baseQuery}
  )
SELECT DISTINCT project_id
FROM sessions
WHERE project_id != 'Unsolved'
ORDER BY project_id
`;

        const projectIdsResponse = await DataLoader.runQuery(
            projectIdsSql,
            queryCache
        );
        const projectIds = (
            projectIdsResponse.result as { project_id: string }[]
        ).map((r) => r.project_id);
        const pivotColumns = projectIds.map((id) => `'${id}'`).join('\n    , ');

        const timePerGameSql = `
WITH
  sessions AS (
${baseQuery}
  )
SELECT *
FROM (
  SELECT date, project_id, ROUND(time, 2) AS time
  FROM sessions
  WHERE project_id != 'Unsolved'
)
PIVOT (
  SUM(time) FOR project_id IN (
    ${pivotColumns}
  )
)
ORDER BY date
`;

        const contentToTotalSql = `
WITH
  sessions AS (
${baseQuery}
  )
  , per_date AS (
    SELECT date
      , ROUND(SUM(time), 2) AS total_time
      , ROUND(SUM(IF(project_id != 'Unsolved', time, 0)) / SUM(time), 4) AS content_time_part
    FROM sessions
    GROUP BY date
  )
SELECT date
  , total_time
  , content_time_part
  , ROUND(total_time * content_time_part, 2) AS total_content_time
FROM per_date
ORDER BY date
`;

        const [timePerGameResponse, contentToTotalResponse] = await Promise.all(
            [
                DataLoader.runQuery(timePerGameSql, queryCache),
                DataLoader.runQuery(contentToTotalSql, queryCache),
            ]
        );

        return {
            timePerGame: timePerGameResponse.result as Record<
                string,
                unknown
            >[],
            contentToTotal: contentToTotalResponse.result as Record<
                string,
                unknown
            >[],
        };
    }
}
